import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { formatPhone } from '../lib/phone';
import { apiFetch, API_BASE_URL } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, ConfirmDialog, Toast, ui, fmtDate,
} from '../components/adminUi';
import Icon from '../components/Icon';
import { userStatusLabel, moderationStatusLabel } from '../lib/userStatus';

// Pending providers are read from the real users endpoint filtered by
// role=provider & moderationStatus=pending (the moderation flag lives on the
// user record). Approve/reject hit the canonical provider moderation paths.

// «Tasdiqlash» o'rniga chiqadigan izoh (backend ADMIN_APPROVAL_DISABLED xabari bilan bir xil).
const MYID_ONLY_TEXT = 'Usta MyID orqali tasdiqlanadi';

const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ') || 'Usta';

// Backend (GET /api/admin/users va /users/:id) qaytaradigan maydonlar: photoUrl (mutlaq https | null),
// categories [{id,name}], categoryName (vergul bilan), regionName, districtName.
const categoryNames = (u) => {
  const fromList = Array.isArray(u?.categories)
    ? u.categories.map((c) => (c && c.name) || '').filter(Boolean)
    : [];
  if (fromList.length) return fromList;
  return u?.categoryName ? String(u.categoryName).split(',').map((x) => x.trim()).filter(Boolean) : [];
};

// Hudud: «Ko'rish» oynasi bilan bir xil maydonlar (regionName / districtName|cityName).
const areaText = (u) => {
  const region = u?.regionName || u?.region_name || '';
  const city = u?.cityName || u?.city_name || u?.districtName || u?.district_name || '';
  return [region, city].filter(Boolean).join(', ') || '—';
};

export default function ModerationPage() {
  const { auth } = useAuth();

  const [items, setItems] = useState([]);
  // GET /api/admin/providers/approval-config. null = hali yuklanmagan yoki xato -> «Tasdiqlash» yashirin (fail-closed).
  const [approvalConfig, setApprovalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [detail, setDetail] = useState(null); // full user profile
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null); // provider being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [approveFor, setApproveFor] = useState(null); // tasdiq dialogi kutayotgan usta
  const [acting, setActing] = useState(false);
  const [toast, setToast] = useState(null);
  // So'rov raqami: modal yopilgandan (yoki qayta ochilgandan) keyin kelgan eski javob
  // modalni QAYTA ochib yubormasin.
  const detailReq = useRef(0);
  const [photoOpen, setPhotoOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiFetch(
        '/api/admin/users?role=provider&moderationStatus=pending&page=1',
        auth.token
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      const list = Array.isArray(data.items) ? data.items : [];
      setItems(list.filter((u) => (u.moderationStatus || '') === 'pending'));
    } catch {
      setItems([]);
      setFetchError('Ustalar ro\'yxatini yuklashda xatolik. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  // Sahifa ochilganda yoki yangilanganda. Xato bo'lsa ro'yxat va «Rad etish» ishlayveradi.
  const loadApprovalConfig = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/providers/approval-config', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setApprovalConfig({ adminApprovalEnabled: data && data.adminApprovalEnabled === true });
    } catch {
      setApprovalConfig(null);
    }
  }, [auth.token]);

  useEffect(() => { loadApprovalConfig(); }, [loadApprovalConfig]);

  // YAGONA shart (qator tugmasi, detail modal, tasdiq dialogi): bayroq true VA element canAdminApprove !== false.
  const canApprove = (u) =>
    !!approvalConfig && approvalConfig.adminApprovalEnabled === true && !(u && u.canAdminApprove === false);

  const closeDetail = () => {
    detailReq.current += 1;
    setPhotoOpen(false);
    setDetail(null);
    setDetailLoading(false);
  };

  const openDetail = async (id) => {
    const req = ++detailReq.current;
    setDetail({ id, __loading: true });
    setDetailLoading(true);
    try {
      const res = await apiFetch(`/api/admin/users/${id}`, auth.token);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || '');
      }
      const data = await res.json();
      if (req === detailReq.current) setDetail(data);
    } catch (e) {
      if (req === detailReq.current) setDetail({ id, __error: e.message || true });
    } finally {
      if (req === detailReq.current) setDetailLoading(false);
    }
  };

  const doAction = async (provider, action, body) => {
    setActing(true);
    try {
      const res = await apiFetch(`/api/admin/providers/${provider.id}/${action}`, auth.token, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) {
        closeDetail();
        setApproveFor(null);
        setRejectFor(null);
        setRejectReason('');
        setToast({ type: 'success', text: action === 'approve' ? 'Usta tasdiqlandi' : 'Usta rad etildi' });
        await load();
        return;
      }
      const d = await res.json().catch(() => ({}));
      setApproveFor(null);
      if (res.status === 403 && (d.code === 'ADMIN_APPROVAL_DISABLED' || d.error === 'ADMIN_APPROVAL_DISABLED')) {
        // Bayroq o'chiq: tugmalarni yashiramiz, umumiy xato emas.
        setApprovalConfig({ adminApprovalEnabled: false });
        setToast({ type: 'error', text: MYID_ONLY_TEXT });
        return;
      }
      setToast({ type: 'error', text: d.message || 'Amalni bajarishda xatolik yuz berdi.' });
    } catch {
      setApproveFor(null);
      setToast({ type: 'error', text: "Tarmoq xatosi. Qayta urinib ko'ring." });
    } finally {
      setActing(false);
    }
  };

  const documents = (u) => {
    // Collect any document-like URLs the backend may expose.
    const urls = [];
    if (Array.isArray(u.documents)) urls.push(...u.documents);
    if (Array.isArray(u.documentUrls)) urls.push(...u.documentUrls);
    ['passportUrl', 'licenseUrl', 'certificateUrl'].forEach((k) => {
      if (u[k]) urls.push(u[k]);
    });
    return urls.filter(Boolean).map(String).map(httpsUrl);
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Ustalar moderatsiyasi"
        subtitle="Tasdiqlashni kutayotgan ustalar"
      >
        <button style={{ ...ui.pagerBtn }} onClick={() => { load(); loadApprovalConfig(); }}><Icon name="refresh" size={14} style={{ marginRight: '6px' }} />Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty icon="shield" title="Tasdiqlashni kutayotgan usta yo'q" hint="Hamma arizalar ko'rib chiqilgan." />
      ) : (
        <div style={ui.tableWrap}>
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={{ ...ui.th, width: '48px' }}>#</th>
                <th style={{ ...ui.th, width: '84px' }}>Rasm</th>
                <th style={ui.th}>F.I.O</th>
                <th style={ui.th}>Telefon</th>
                <th style={ui.th}>Kategoriya</th>
                <th style={ui.th}>Hudud</th>
                <th style={ui.th}>MyID</th>
                <th style={ui.th}>Ariza sanasi</th>
                <th style={{ ...ui.th, textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u, i) => (
                <tr key={u.id} style={ui.tr}>
                  <td style={{ ...ui.td, color: 'var(--color-text-muted)' }}>{i + 1}</td>
                  <td style={ui.td}>
                    <div style={d.thumbCell}>
                      <Photo url={u.photoUrl} size={44} alt={fullName(u)} />
                      {!u.photoUrl && <span style={d.noPhotoCaption}>Rasm yo'q</span>}
                    </div>
                  </td>
                  <td style={{ ...ui.td, fontWeight: '600', color: 'var(--color-text)' }}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td style={ui.td}>{formatPhone(u.phone) || '—'}</td>
                  <td style={ui.td}>{categoryNames(u).join(', ') || '—'}</td>
                  <td style={ui.td}>{areaText(u)}</td>
                  <td style={ui.td}>
                    {u.myidVerified
                      ? <Badge tone="green">Tasdiqlangan</Badge>
                      : <Badge tone="primary">Tasdiqlanmagan</Badge>}
                  </td>
                  <td style={ui.td}>{fmtDate(u.createdAt)}</td>
                  <td style={{ ...ui.td, textAlign: 'right' }}>
                    <div style={ui.actions}>
                      <button style={{ ...ui.btnSm, ...ui.btnBlue }} onClick={() => openDetail(u.id)}>
                        Ko'rish
                      </button>
                      {canApprove(u) ? (
                        <button
                          style={{ ...ui.btnSm, ...ui.btnGreen }}
                          onClick={() => setApproveFor(u)}
                          disabled={acting}
                        >
                          Tasdiqlash
                        </button>
                      ) : (
                        <span style={d.myidNote}>{MYID_ONLY_TEXT}</span>
                      )}
                      <button
                        style={{ ...ui.btnSm, ...ui.btnRed }}
                        onClick={() => { setRejectFor(u); setRejectReason(''); }}
                        disabled={acting}
                      >
                        Rad etish
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal: yuklanish / xato / muvaffaqiyat — uchalasida ham Yopish, ×, Escape va fon ishlaydi */}
      {detail && (
        <Modal title="Usta ma'lumotlari" onClose={closeDetail} maxWidth="520px">
          {detailLoading || detail.__loading ? (
            <>
              <Loading />
              <div style={ui.modalActions}>
                <button style={ui.cancelBtn} onClick={closeDetail}>Yopish</button>
              </div>
            </>
          ) : detail.__error ? (
            <>
              <ErrorBanner>
                {typeof detail.__error === 'string' ? detail.__error : "Ma'lumotni yuklab bo'lmadi."}
              </ErrorBanner>
              <div style={ui.modalActions}>
                <button style={ui.cancelBtn} onClick={closeDetail}>Yopish</button>
                <button style={ui.saveBtn} onClick={() => openDetail(detail.id)}>Qayta urinish</button>
              </div>
            </>
          ) : (
            <>
              <div style={d.photoBlock}>
                <Photo
                  url={detail.photoUrl}
                  size={120}
                  alt={fullName(detail)}
                  onClick={() => setPhotoOpen(true)}
                />
                <div style={d.photoSide}>
                  <div style={ui.label}>Profil rasmi</div>
                  {detail.photoUrl ? (
                    <div style={d.photoHint}>Rasmni to'liq ko'rish uchun bosing</div>
                  ) : (
                    <div style={d.photoHint}>Rasm yo'q</div>
                  )}
                </div>
              </div>

              <div className="th-grid-2" style={d.grid}>
                <Info label="F.I.O" value={[detail.firstName, detail.lastName].filter(Boolean).join(' ')} />
                <Info label="Telefon" value={formatPhone(detail.phone)} />
                <Info label="Holat" value={userStatusLabel(detail.status)} />
                <Info label="Moderatsiya" value={moderationStatusLabel(detail.moderationStatus)} />
                <Info label="MyID" value={detail.myidVerified ? 'Tasdiqlangan' : 'Tasdiqlanmagan'} />
                <Info label="Ariza sanasi" value={fmtDate(detail.createdAt, true)} />
                <Info label="Viloyat / shahar" value={detail.regionName} />
                <Info label="Tuman" value={detail.districtName} />
              </div>

              <div style={d.docsBlock}>
                <div style={ui.label}>Kategoriya(lar)</div>
                {categoryNames(detail).length === 0 ? (
                  <div style={d.infoValue}>Ko'rsatilmagan</div>
                ) : (
                  <div style={d.chips}>
                    {categoryNames(detail).map((name) => (
                      <span key={name} style={d.chip}>{name}</span>
                    ))}
                  </div>
                )}
              </div>

              <div style={d.docsBlock}>
                <div style={ui.label}>Hujjatlar</div>
                {documents(detail).length === 0 ? (
                  <div style={d.noDocs}>Hujjat yuklanmagan</div>
                ) : (
                  <div style={d.docsGrid}>
                    {documents(detail).map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" style={d.docLink}>
                        <img src={url} alt={`hujjat ${i + 1}`} style={d.docImg} />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ ...ui.modalActions, flexWrap: 'wrap' }}>
                <button style={ui.cancelBtn} onClick={closeDetail} disabled={acting}>Yopish</button>
                <button
                  style={{ ...ui.btnRed, ...ui.cancelBtn, color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                  onClick={() => { setRejectFor(detail); setRejectReason(''); }}
                  disabled={acting}
                >
                  Rad etish
                </button>
                {canApprove(detail) ? (
                  <button
                    style={ui.saveBtn}
                    onClick={() => setApproveFor(detail)}
                    disabled={acting}
                  >
                    Tasdiqlash
                  </button>
                ) : (
                  <span style={d.myidNote}>{MYID_ONLY_TEXT}</span>
                )}
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Profil rasmini to'liq o'lchamda ochish */}
      {photoOpen && detail && detail.photoUrl && (
        <Lightbox url={detail.photoUrl} alt={fullName(detail)} onClose={() => setPhotoOpen(false)} />
      )}

      {/* Tasdiq dialogi */}
      {approveFor && canApprove(approveFor) && (
        <ConfirmDialog
          title="Ustani tasdiqlash"
          message={`${fullName(approveFor)} ustasini tasdiqlaysizmi?`}
          confirmLabel="Tasdiqlash"
          busy={acting}
          onCancel={() => setApproveFor(null)}
          onConfirm={() => doAction(approveFor, 'approve')}
        />
      )}

      {/* Reject modal with reason */}
      {rejectFor && (
        <Modal title="Rad etish sababi" onClose={() => setRejectFor(null)} maxWidth="420px" busy={acting}>
          <div style={ui.field}>
            <label style={ui.label}>Sabab <span style={ui.req}>*</span></label>
            <textarea
              style={ui.textarea}
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Rad etish sababini yozing"
              disabled={acting}
              autoFocus
            />
          </div>
          <div style={ui.modalActions}>
            <button style={ui.cancelBtn} onClick={() => setRejectFor(null)} disabled={acting}>
              Bekor qilish
            </button>
            <button
              style={{ ...ui.saveBtn, background: 'var(--color-danger)', ...(acting || !rejectReason.trim() ? ui.btnDisabled : {}) }}
              onClick={() => doAction(rejectFor, 'reject', { reason: rejectReason.trim() })}
              disabled={acting || !rejectReason.trim()}
            >
              {acting ? 'Bajarilmoqda...' : 'Rad etish'}
            </button>
          </div>
        </Modal>
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </AdminLayout>
  );
}

// Usta rasmi: yo'q yoki yuklanmasa — avatar (placeholder). `onClick` bo'lsa va rasm bor — bosiladigan tugma.
function Photo({ url, size, alt, onClick }) {
  const [failed, setFailed] = useState(false);
  const src = url ? httpsUrl(String(url)) : '';
  useEffect(() => { setFailed(false); }, [src]);
  const box = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: size > 60 ? 'var(--radius-card)' : '999px',
    flexShrink: 0,
    overflow: 'hidden',
    background: 'var(--color-surface-alt)',
    border: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-muted)',
    boxSizing: 'border-box',
    padding: 0,
  };
  if (!src || failed) {
    return <div style={box} aria-label="Rasm yo'q"><Icon name="user" size={Math.round(size * 0.5)} /></div>;
  }
  const img = (
    <img
      src={src}
      alt={alt}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      onError={() => setFailed(true)}
    />
  );
  if (!onClick) return <div style={box}>{img}</div>;
  return (
    <button type="button" style={{ ...box, cursor: 'zoom-in' }} onClick={onClick} title="To'liq o'lchamda ochish">
      {img}
    </button>
  );
}

function Lightbox({ url, alt, onClose }) {
  const src = httpsUrl(String(url));
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    // Capture bosqichida: Escape faqat rasmni yopsin, orqadagi modalni emas.
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  return (
    <div style={d.lightbox} onClick={onClose} role="dialog" aria-modal="true">
      <button type="button" style={d.lightboxX} onClick={onClose} aria-label="Yopish" title="Yopish">
        <Icon name="x" size={20} />
      </button>
      <img src={src} alt={alt} style={d.lightboxImg} onClick={(e) => e.stopPropagation()} />
      <a href={src} target="_blank" rel="noreferrer" style={d.lightboxLink} onClick={(e) => e.stopPropagation()}>
        Yangi oynada ochish
      </a>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={d.infoLabel}>{label}</div>
      <div style={d.infoValue}>{value === null || value === undefined || value === '' ? '—' : value}</div>
    </div>
  );
}

// Force absolute https so document images load reliably.
function httpsUrl(u) {
  if (!u) return u;
  if (u.startsWith('http://')) return 'https://' + u.slice(7);
  if (u.startsWith('/')) return API_BASE_URL + u;
  return u;
}

const d = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px 20px',
    marginBottom: '18px',
  },
  infoLabel: { fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '2px' },
  infoValue: { fontSize: '14px', color: 'var(--color-text)', fontWeight: '500' },
  myidNote: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--color-text-muted)',
    background: 'var(--color-surface-alt)',
    borderRadius: '999px',
    padding: '6px 12px',
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  thumbCell: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' },
  noPhotoCaption: { fontSize: '11px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' },
  photoBlock: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '18px' },
  photoSide: { minWidth: 0 },
  photoHint: { fontSize: '13px', color: 'var(--color-text-muted)' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' },
  chip: {
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    borderRadius: '999px',
    padding: '4px 12px',
    fontSize: '13px',
    fontWeight: '700',
  },
  lightbox: {
    position: 'fixed',
    inset: 0,
    zIndex: 500,
    background: 'rgba(17,17,17,0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '24px',
    cursor: 'zoom-out',
  },
  lightboxImg: {
    maxWidth: '92vw',
    maxHeight: '80vh',
    objectFit: 'contain',
    borderRadius: 'var(--radius-card)',
    background: 'var(--color-surface)',
    cursor: 'default',
  },
  lightboxLink: { color: 'var(--color-primary)', fontSize: '14px', fontWeight: '700' },
  lightboxX: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '44px',
    height: '44px',
    borderRadius: '999px',
    border: 'none',
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  docsBlock: { marginBottom: '16px' },
  noDocs: {
    fontSize: '13px',
    color: 'var(--color-text-muted)',
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-input)',
    padding: '14px',
    textAlign: 'center',
  },
  docsGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  docLink: { display: 'block' },
  docImg: {
    width: '90px',
    height: '90px',
    objectFit: 'cover',
    borderRadius: 'var(--radius-btn)',
    border: '1px solid var(--color-border)',
  },
};
